/**
 * Password Strength Indicator Component
 * Shows visual feedback for password strength
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { getPasswordStrength } from '../../utils/validation';

interface PasswordStrengthIndicatorProps {
  password: string;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
}) => {
  const { t } = useTranslation('auth');

  if (!password) return null;

  const { score, label, color } = getPasswordStrength(password);

  return (
    <div className="mt-2">
      {/* Strength bars */}
      <div className="flex space-x-1 mb-1">
        {[0, 1, 2, 3, 4].map((index) => (
          <div
            key={index}
            className={clsx(
              'h-1 flex-1 rounded transition-colors duration-200',
              index <= score ? color : 'bg-gray-200'
            )}
          />
        ))}
      </div>
      {/* Strength label */}
      <p className="text-xs text-gray-600">
        {t('passwordStrength.label')}: <span className="font-medium">{label}</span>
      </p>
      {/* Requirements checklist */}
      <ul className="mt-2 text-xs text-gray-500 space-y-1">
        <li className={clsx(password.length >= 8 ? 'text-green-600' : '')}>
          {password.length >= 8 ? '\u2713' : '\u2022'} {t('passwordStrength.requirements.minLength')}
        </li>
        <li className={clsx(/[A-Z]/.test(password) ? 'text-green-600' : '')}>
          {/[A-Z]/.test(password) ? '\u2713' : '\u2022'} {t('passwordStrength.requirements.uppercase')}
        </li>
        <li className={clsx(/[a-z]/.test(password) ? 'text-green-600' : '')}>
          {/[a-z]/.test(password) ? '\u2713' : '\u2022'} {t('passwordStrength.requirements.lowercase')}
        </li>
        <li className={clsx(/[0-9]/.test(password) ? 'text-green-600' : '')}>
          {/[0-9]/.test(password) ? '\u2713' : '\u2022'} {t('passwordStrength.requirements.number')}
        </li>
      </ul>
    </div>
  );
};

export default PasswordStrengthIndicator;
